import logger from "@airhelp/node-logger";
import { Button, Checkbox, Flex, Heading, Input, Text } from "@chakra-ui/react";
import type { AxiosError } from "axios";
import axios from "axios";
import config from "config/config";
import { useReferralProgramConfig } from "contexts/DataContext/hooks";
import { useUserContext } from "contexts/HelpAFriend/UserContext";
import { useLanguage, useLocalizedData, useTranslations } from "contexts/LocalizationContext/hooks";
import { useRef, useState } from "react";
import { getPageUrl } from "utils/getPageUrl";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";

const MODAL_GENERATE_LINK_EVENT_UA = ["clicked", "modal", "Generate_link"] as const;
const MODAL_GENERATE_LINK_EVENT = { event: "help_friend", label: "generate_link", placement: "modal" } as const;

const MODAL_REGISTER_SUCCESS_EVENT_UA = ["registeredToReferral", "modal", "lp_modal", "success"] as const;
const MODAL_REGISTER_SUCCESS_EVENT = {
  event: "help_friend",
  label: "registration_success",
  placement: "modal",
} as const;

const MODAL_REGISTER_ERROR_EVENT_UA = ["registeredToReferral", "lp_modal", "lp_modal", "error"] as const;
const MODAL_REGISTER_ERROR_EVENT = { event: "help_friend", label: "registration_error", placement: "modal" } as const;

const {
  iterable: {
    iterableListIds: { blog: blogListId },
  },
} = config;

export const NewUserView = () => {
  const translations = useTranslations();
  const { getLocalizedCurrency } = useLocalizedData();
  const language = useLanguage();
  const { updateShareLink, updateEmail, userEmail, setUserEmail } = useUserContext();

  const [subscribeFormState, setSubscribeFormState] = useState({
    marketingConsent: false,
    privacyConsent: false,
    error: "",
    isLoading: false,
  });

  const emailInputRef = useRef(null);

  const termsTranslationVariables = {
    "%terms-link-attrs%": `href="${getPageUrl(
      language,
      "referralTerms"
    )}" target="_blank" rel="noopener noreferrer" title="${translations(
      "modal_terms_title"
    )}" style="text-decoration: underline"`,
    "%privacy-link-attrs%": `href="${getPageUrl(
      language,
      "privacy"
    )}" target="_blank" rel="noopener noreferrer" title="${translations(
      "modal_terms_privacy_title"
    )}" style="text-decoration: underline"`,
  };

  const referralProgramConfig = useReferralProgramConfig();
  const { campaignId, campaignSource, commissionValue } = referralProgramConfig;

  const handleEmailChange = ({ target: { value: userEmail } }) => setUserEmail(userEmail);

  const trackRegisterSuccess = () => {
    trackUAEvent(MODAL_REGISTER_SUCCESS_EVENT_UA);
    trackCustomEvent(MODAL_REGISTER_SUCCESS_EVENT);
  };

  const trackRegisterError = (error: AxiosError) => {
    trackUAEvent(MODAL_REGISTER_ERROR_EVENT_UA);
    trackCustomEvent(MODAL_REGISTER_ERROR_EVENT);
    logger.error("Error while Register New User", error);
  };

  const handleSubmit = async () => {
    if (emailInputRef && subscribeFormState.privacyConsent) {
      setSubscribeFormState((prevState) => ({ ...prevState, isLoading: true }));
      trackUAEvent(MODAL_GENERATE_LINK_EVENT_UA);
      trackCustomEvent(MODAL_GENERATE_LINK_EVENT);

      try {
        const payload = {
          campaignId,
          language,
          userEmail,
          campaignSource,
          marketingConsent: subscribeFormState.marketingConsent,
          listId: blogListId,
        };
        const {
          data: { shareUrl },
        } = await axios.post<{ shareUrl: string }>("/api/refer-a-friend/", payload);

        setSubscribeFormState((prevState) => ({ ...prevState, isLoading: false }));
        updateShareLink(shareUrl);
        updateEmail(userEmail);
        trackRegisterSuccess();
      } catch (error) {
        setSubscribeFormState((prevState) => ({
          ...prevState,
          isLoading: false,
          error: translations("modal_something_went_wrong"),
        }));
        trackRegisterError(error);
      }
    }
  };

  return (
    <>
      <Heading data-testid="modal-heading" variant="h3" as="h3" mb={3} maxW={{ base: "100%", md: "350px" }}>
        {translations("modal_help_friends_get_rewarded")}
      </Heading>
      <Text
        mb={10}
        dangerouslySetInnerHTML={{
          __html: translations("modal_enter_email", {
            "%commission%": getLocalizedCurrency(Number(commissionValue), "EUR"),
          }),
        }}
      />
      <Input
        data-testid="modal-input"
        required
        type="email"
        ref={emailInputRef}
        value={userEmail}
        onChange={handleEmailChange}
        mb={6}
        focusBorderColor="primary.500"
        placeholder={translations("enter_email_placeholder")}
      />
      <Checkbox
        data-testid="privacy-consent-checkbox"
        colorScheme="primary"
        mb={4}
        size="lg"
        onChange={({ target: { checked: privacyConsent } }) =>
          setSubscribeFormState((prevState) => ({ ...prevState, privacyConsent }))
        }
      >
        <Text
          fontSize="sm"
          textAlign="start"
          dangerouslySetInnerHTML={{
            __html: translations("agree_to_terms", termsTranslationVariables),
          }}
        />
      </Checkbox>
      <Flex alignItems="flex-start" mb={{ base: 6, md: 10 }}>
        <Checkbox
          data-testid="marketing-consent-checkbox"
          name="marketingConsent"
          aria-labelledby="marketing-consent"
          size="lg"
          mr={2}
          onChange={({ target: { checked: marketingConsent } }) =>
            setSubscribeFormState((prevState) => ({ ...prevState, marketingConsent }))
          }
        />
        <Text id="marketing-consent" color="inherit" fontSize="sm" textAlign="start">
          {translations("marketing_consent")}
        </Text>
      </Flex>
      <Button
        data-testid="modal-generate-link"
        minWidth="100%"
        title={translations("modal_generate_link")}
        onClick={handleSubmit}
        isDisabled={!subscribeFormState.privacyConsent}
        isLoading={subscribeFormState.isLoading}
      >
        {translations("modal_generate_link")}
      </Button>
      {subscribeFormState.error && (
        <Text mt={4} color="danger.500">
          {subscribeFormState.error}
        </Text>
      )}
    </>
  );
};
